/**
 * IA Cart Upsell - Re-renders when the cart contents change.
 *
 * Products come from an AB Tasty strategy when one is configured on the block,
 * otherwise from Shopify's own recommendations. The merchant's handpicked
 * upsells are the last fallback either way.
 */

import { EVENTS, subscribe } from "ia-util-pubsub";
import { captureError } from "ia-error-report";
import { readCached, writeCached, ttlFromAttribute } from "ia-reco-cache";
import { isGrantedLine, isSampleLine } from "ia-cart-api";
import { debugEnabled, shopRoot } from "ia-util-helpers";

const RECO_CARD_SECTION = 'ia-reco-cart-cards';
const RECO_TIMEOUT_MS = 4000;
// Below this many surviving cards the strip counts as starved by the filter.
const MIN_RECO_CARDS = 2;
// From 780 the panel is a vertical column, so keen must not measure it.
// Mirrors the breakpoint in ia-cart-upsell.css; change them together.
const SLIDER_MQ = window.matchMedia('(max-width: 779px)');
// The theme's desktop breakpoint, which the block's visibility setting rides.
const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

// Dev logging: the proxy payload and its answer, which Sentry cannot carry.
const DEBUG = debugEnabled();

function debug(label, data) {
  if (DEBUG) console.log(`[ia-cart-upsell] ${label}`, data);
}

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const MOVE_MS = 360;
const ENTER_MS = 280;
const EXIT_MS = 240;
// One curve for every strip movement, or the fates read as separate effects.
const STRIP_EASE = 'cubic-bezier(0.2, 0, 0, 1)';

/**
 * The leaving card folds shut and the cards below ride the layout up into its
 * place, continuously. The final margin swallows the column's own gap, or the
 * removal would still end on a 16px snap.
 */
function collapseCard(card, onDone) {
  const gap = parseFloat(getComputedStyle(card.parentElement).rowGap) || 0;
  card.style.overflow = 'hidden';
  card.style.pointerEvents = 'none';
  const fold = card.animate(
    [
      { height: `${card.getBoundingClientRect().height}px`, opacity: 1, marginTop: '0px' },
      { height: '0px', opacity: 0, marginTop: `-${gap}px` },
    ],
    { duration: EXIT_MS, easing: 'ease-in' }
  );
  fold.onfinish = onDone;
  // a cancel (the strip rebuilt mid-fold) must still take the card out
  fold.oncancel = onDone;
}

/**
 * Both strip mutations (the instant drop and the rebuilt list) run through
 * here, or one of them stays abrupt. Measured before, mutated, and the three
 * fates animated after: a card that stayed GLIDES
 * to its new place instead of teleporting, a card that entered FADES in, and a
 * card that left needs nothing, its space closes by the neighbours' glide.
 */
function animateStrip(slides, mutate, wants) {
  if (!wants.move && !wants.enter) {
    mutate();
    return;
  }
  const before = new Map();
  [...slides.children].forEach((card) => before.set(card, card.getBoundingClientRect().top));
  mutate();
  const now = [...slides.children];
  const fresh = now.filter((card) => !before.has(card));
  // Nothing carried over: a different offer arrives as one short cascade.
  const wholesale = now.length > 0 && fresh.length === now.length;
  now.forEach((card, index) => {
    if (before.has(card)) {
      if (!wants.move) return;
      const shift = before.get(card) - card.getBoundingClientRect().top;
      if (!shift) return;
      card.animate(
        [{ transform: `translateY(${shift}px)` }, { transform: 'none' }],
        { duration: MOVE_MS, easing: STRIP_EASE }
      );
      return;
    }
    if (!wants.enter) return;
    card.animate(
      [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
      {
        duration: ENTER_MS,
        // The kept cards start settling first, the newcomers join a beat
        // later: sequenced reads calmer than everything landing on one frame.
        delay: wholesale ? Math.min(index * 50, 200) : 120,
        easing: STRIP_EASE,
        // holds the card invisible through its delay
        fill: 'backwards',
      }
    );
  });
}

// Shopify stamps these into every product link the search endpoint renders,
// and `_pos`/`_sid` differ on every single request. Comparing markup that
// carries them means no card ever matches its own refetched copy.
const SEARCH_NOISE = ['_pos', '_sid', '_ss', '_psq', '_v'];

/**
 * What makes two cards the same card for reconciliation: the markup minus what
 * WE stamp at runtime and what the search endpoint re-randomises. Without this
 * a card never matches its fresh copy and gets replaced, which is the repaint
 * being avoided. A variant the shopper picked in the select survives too.
 */
function cardContent(card) {
  const copy = card.cloneNode(true);
  copy.removeAttribute('data-in-bag');
  copy.querySelectorAll('[disabled]').forEach((node) => node.removeAttribute('disabled'));
  // Stamped by the image's own onload (ia-responsive-image.liquid), so every
  // card that finished loading stopped matching its freshly fetched copy and
  // was replaced: the blink this whole comparison exists to prevent.
  copy.querySelectorAll('.ia-img-loaded').forEach((node) => node.classList.remove('ia-img-loaded'));
  copy.querySelectorAll('a[href]').forEach((link) => {
    try {
      // Any base works: only the path and the surviving query are compared,
      // and a constant keeps this readable off a real page.
      const url = new URL(link.getAttribute('href'), 'https://x.invalid');
      SEARCH_NOISE.forEach((key) => url.searchParams.delete(key));
      link.setAttribute('href', url.pathname + url.search);
    } catch {
      // not a parseable href: leave it, it compares as-is
    }
  });
  return copy.innerHTML;
}

/**
 * The last answer is replayed across pages while the cart signature matches
 * (the same request fired on every load). The TTL bounds cached price/stock.
 * The store itself is shared with the sliders and the PLP order: ia-reco-cache.
 */
const RECO_CACHE_SLOT = 'cart';
// The picks (ids from the proxy) keep for hours; the cards (Shopify markup with
// prices in it) do not keep at all unless a merchant says otherwise.
const RECO_PICKS_TTL_MS = 12 * 60 * 60 * 1000;
const RECO_CARDS_TTL_MS = 0;

/** AB Tasty recommendations, rendered through Shopify. See docs/abtasty-integration.md. */
class AbTastyAPI {
  /**
   * One retry on a server-side or transport failure. The App Proxy in front of
   * the worker answers 500 when it cannot reach it, and the worker itself only
   * ever returns 502/503, so a 5xx here means "try again", not "no data". A
   * single hiccup right after an add-to-cart otherwise leaves the panel empty
   * until the next cart change. Timeouts are NOT retried: the shopper has
   * already waited, and a slow upstream stays slow.
   */
  static async fetch(variantIds, options) {
    if (!options?.endpoint || !options?.strategy) return [];

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const outcome = await this.attempt(variantIds, options);
      if (!outcome.retriable || attempt === 1) return outcome.value;
      debug('retrying', { status: outcome.status || 'transport' });
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
    return null;
  }

  static async attempt(variantIds, { endpoint, strategy, source, picks }) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RECO_TIMEOUT_MS);

    try {
      // WHICH products were picked is an id list that costs a proxy call and
      // holds no prices, so it is cached for hours. HOW they look is rendered
      // by Shopify below, and that markup carries price and stock: its own,
      // much shorter TTL lives on the element.
      const cachedPicks = picks && readCached(picks.slot, picks.signature, picks.ttl);
      const handles = cachedPicks
        ? JSON.parse(cachedPicks)
        : await this.handles(variantIds, { endpoint, strategy, source }, controller.signal);
      if (!handles.length) return { value: [] };
      if (picks && !cachedPicks) writeCached(picks.slot, picks.signature, picks.ttl, JSON.stringify(handles));

      const cards = await this.cards(handles, controller.signal);
      // Search answers in relevance order; restore the AB Tasty order.
      const value = handles.map((handle) => cards.get(handle)).filter(Boolean);
      // A strategy's list is one batch: the same request returns the same
      // handles, so there is nothing to refill from. When the hidden-types
      // filter (ia-product-visible) ate most of it, the team hears; the fallback
      // products cover the shopper.
      if (value.length < MIN_RECO_CARDS && handles.length >= MIN_RECO_CARDS) {
        const starved = new Error(
          `recommendations starved: ${handles.length} sent, ${value.length} left after the hidden-types filter`
        );
        console.error('[ia-cart-upsell]', starved.message);
        // ids and handles only, truncated: enough to replay the request and
        // see WHICH recommendations were eaten, never prices or names
        captureError(starved, {
          where: 'cart:reco-filtered',
          sent: handles.length,
          kept: value.length,
          cart: variantIds.slice(0, 10).join(','),
          recommended: handles.slice(0, 10).join(','),
          left: value.slice(0, 10).map((card) => card.dataset.productHandle).join(','),
        });
      }
      return { value };
    } catch (error) {
      // An abort is read off the signal as well as the name: WebKit answers an
      // abort that lands mid-body with TypeError "Load failed", not AbortError.
      const aborted = error.name === 'AbortError' || controller.signal.aborted;
      // The panel hides itself on an empty result, so without this the failure
      // would be invisible to everyone, shopper and team alike.
      if (!aborted) {
        captureError(error, {
          where: 'cart:recommendations',
          status: error.status,
          strategy: strategy || '',
          route: source === 'repurchase' ? 'repurchase' : 'recos',
        });
      }
      // null rather than an empty array, and the difference carries weight: the
      // caller keeps whatever is already on screen for null, and only an honest
      // empty answer clears the panel.
      const retriable = !aborted && (!error.status || error.status >= 500);
      return { value: null, retriable, status: error.status };
    } finally {
      clearTimeout(timer);
    }
  }

  static async handles(variantIds, { endpoint, strategy, source }, signal) {
    // Same contract as ia-reco-slider: a repurchase strategy gets its context
    // assembled on the proxy from the shopper's own orders, so the only
    // difference here is which route answers, and no cart variables are sent
    // (they would only split the proxy cache for an answer that ignores them).
    const repurchase = source === 'repurchase';
    const route = repurchase ? 'repurchase' : 'recos';
    const params = new URLSearchParams({ strategy });
    let variables = null;
    if (!repurchase && variantIds.length) {
      // Sorted so one cart is one cache key: the proxy keys on the literal
      // variables string, and AB Tasty ignores the order (verified).
      const items = variantIds.map(String).sort();
      variables = JSON.stringify({ cart_items: items });
      params.set('variables', variables);
    }

    const url = `${endpoint.replace(/\/$/, '')}/${route}?${params}`;
    debug('sent', { url, strategy, variables });

    const response = await fetch(url, { signal });
    if (!response.ok) {
      const body = await response.text().catch(() => '<body could not be read>');
      debug('proxy failed', { status: response.status, body });
      const error = new Error(`Recommendation proxy responded ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    debug('received', data);
    return Array.isArray(data?.items) ? data.items.map((item) => item.handle).filter(Boolean) : [];
  }

  static async cards(handles, signal) {
    const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
    const url = `/search?type=product&q=${encodeURIComponent(query)}&section_id=${RECO_CARD_SECTION}`;
    const response = await fetch(url, { signal });
    if (!response.ok) {
      debug('card lookup failed', { status: response.status, url });
      const error = new Error(`Card lookup responded ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cards = new Map();
    doc.querySelectorAll('.ia-cart-upsell__item-wrapper').forEach((card) => {
      cards.set(card.dataset.productHandle, card);
    });
    return cards;
  }
}

// API handler for fetching product recommendations
class RecommendationsAPI {
  /**
   * The hidden-types filter (ia-product-visible) eats cards server-side, so a
   * starved answer asks again with a raised limit: at most two refills, capped
   * at the API's own maximum of 10. Refilling cannot help past that; the team
   * hears and the fallback products cover the shopper. An empty answer is
   * another matter: `complementary` only answers where Search & Discovery has
   * complementary products set up, so it falls back to `related`, which
   * Shopify always computes; nothing related either is not a fault.
   */
  static async fetch(productId, { limit, intent, sectionId }) {
    if (!productId) return [];

    let asked = limit;
    let kind = intent;
    for (let refills = 0; ; refills += 1) {
      const page = await this.page(productId, { limit: asked, intent: kind, sectionId });
      if (page === null) return null;
      const { items, count } = page;
      if (count === 0) {
        if (kind === 'related') return items;
        debug('no complementary products set up, asking for related', { base: productId });
        kind = 'related';
        refills -= 1;
        continue;
      }
      if (items.length >= Math.min(MIN_RECO_CARDS, limit)) return items;
      if (refills >= 2 || asked >= 10) {
        if (items.length < MIN_RECO_CARDS) {
          const starved = new Error(
            `recommendations starved: asked ${asked}, ${items.length} left after the hidden-types filter`
          );
          console.error('[ia-cart-upsell]', starved.message);
          captureError(starved, {
            where: 'cart:reco-filtered',
            asked,
            kept: items.length,
            base: String(productId),
            left: items.slice(0, 10).map((card) => card.dataset.productHandle).join(','),
          });
        }
        return items;
      }
      asked = Math.min(10, asked * 2);
      debug('refilling recommendations', { round: refills + 1, asked });
    }
  }

  static async page(productId, { limit, intent, sectionId }) {
    try {
      const url = `${shopRoot()}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${sectionId}`;
      const response = await fetch(url);
      if (!response.ok) {
        debug('shopify recommendations failed', { status: response.status, url });
        return null;
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = Array.from(doc.querySelectorAll('.ia-cart-upsell__item-wrapper'));
      // What the API sent before the filter (the section root says); a render
      // without the mark counts what survived.
      const root = doc.querySelector('[data-reco-count]');
      const count = root ? parseInt(root.dataset.recoCount, 10) || 0 : items.length;
      return { items, count };
    } catch (error) {
      debug('shopify recommendations failed', error);
      captureError(error, { where: 'cart:recommendations:shopify' });
      // Same contract as AbTastyAPI: null means "could not tell", not "none".
      return null;
    }
  }
}

/**
 * Variant select on an upsell card. It only moves values that Liquid already
 * produced: the chosen id into the buy form, and the price string that the option
 * carries into the button. No money is formatted here.
 */
if (!customElements.get('ia-upsell-card')) {
  class IaUpsellCard extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-upsell-variant]');
      this.select?.addEventListener('change', this.handleChange.bind(this));
    }

    handleChange() {
      const option = this.select.selectedOptions[0];
      if (!option) return;

      const idInput = this.querySelector('input[name="id"]');
      if (idInput) idInput.value = option.value;

      const price = this.querySelector('.ia-price__current, [data-price-current], .ia-price');
      if (price && option.dataset.price) price.textContent = option.dataset.price;
    }
  }
  customElements.define('ia-upsell-card', IaUpsellCard);
}

if (!customElements.get('ia-cart-upsell')) {

  class IaCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.baseProductId = this.dataset.baseProductId || '';
      this.cartProductIds = [];
      // AB Tasty keys on variant ids; the in-cart filter below uses product ids.
      this.cartVariantIds = [];
      this.enableDynamic = this.dataset.enableDynamic === 'true';
      this.sliderTemplate = null;
    }

    connectedCallback() {
      this.cartProductIds = this.getUniqueIds((this.dataset.cartProductIds || '').split(',').filter(id => id.trim()));
      this.cartVariantIds = this.getUniqueIds((this.dataset.cartVariantIds || '').split(',').filter(id => id.trim()));
      // Two lists on purpose: context (above) is shopper-chosen lines only, but
      // pruning must see the WHOLE bag, or a granted product keeps its card.
      this.allProductIds = this.getUniqueIds(
        (this.dataset.allProductIds || this.dataset.cartProductIds || '').split(',').filter(id => id.trim())
      );
      
      // Save the original slider template
      const slider = this.querySelector('ia-slider[data-track="products"]');
      if (slider) {
        this.sliderTemplate = slider.outerHTML;
      }

      // The merchant's picks, pristine: the server only ever renders those, so
      // the boot markup is the one moment they are cleanly separable from
      // whatever the client shows later. Refreshed from each cart re-render.
      if (!this.fallbackCards) this.captureFallbacks(null);

      // Boot render once per instance: the drawer teleports to <body> on
      // connect, and an unguarded render here hit the recommendations proxy
      // twice on every page load.
      if (!this.iaBootRendered && !this.hiddenHere) {
        this.iaBootRendered = true;
        this.checkContextMirror();
        this.render();
      }
      // Crossing into a viewport that shows the panel is the first moment its
      // cards are worth fetching.
      this._onVisibilityBreakpoint = () => {
        if (this.iaBootRendered || this.hiddenHere) return;
        this.iaBootRendered = true;
        this.checkContextMirror();
        this.render();
      };
      DESKTOP_MQ.addEventListener('change', this._onVisibilityBreakpoint);
      this.unsubscribe = subscribe(EVENTS.cartChanged, this.handleCartChanged.bind(this));
      // A failed add never fires cartChanged, so the tap-dim below would stick:
      // markInBag recomputes from the bag truth and wakes the card back up.
      this.unsubscribeError = subscribe(EVENTS.cartError, () => this.markInBag());

      // The tap answers at once: the card dims and its buttons die the moment
      // the shopper commits, not when the server confirms. cartChanged (or
      // cartError above) then makes the truth stick. One binding per instance.
      if (!this.iaTapBound) {
        this.iaTapBound = true;
        this.addEventListener('click', (event) => {
          if (!this.hideDuplicates) return;
          const button = event.target.closest('button[name="add"]');
          const card = button?.closest('.ia-cart-upsell__item-wrapper');
          if (!card || !this.contains(card)) return;
          // The attribute lands synchronously (instant dim + pointer-events);
          // .disabled waits a tick, or the submit this click is about to fire
          // dies against a disabled submit button.
          card.toggleAttribute('data-in-bag', true);
          setTimeout(() => {
            card.querySelectorAll('button').forEach((b) => { b.disabled = true; });
          }, 0);
        });
      }

      this._onBreakpoint = () => this.syncSlider();
      SLIDER_MQ.addEventListener('change', this._onBreakpoint);
    }

    /** True where the block's `Show on` setting hides this panel: no
     *  recommendations are requested for a viewport nobody sees them on. */
    get hiddenHere() {
      const shownOn = this.closest('[data-visibility]')?.dataset.visibility;
      if (shownOn === 'desktop') return !DESKTOP_MQ.matches;
      if (shownOn === 'mobile') return DESKTOP_MQ.matches;
      return false;
    }

    /** Keen runs only where the cards are a horizontal strip, and only on the
     *  visible track: a display:none slider measures every slide as zero. */
    syncSlider() {
      this.querySelectorAll('ia-slider').forEach((slider) => {
        const active = slider === this.activeTrack;
        if (active && SLIDER_MQ.matches) {
          // Optional: a section re-render can hand us an <ia-slider> before the
          // element upgrades. Its own connectedCallback initialises it then, so
          // skipping here loses nothing and stops a rejection reaching Sentry.
          if (!slider.slider) slider.initializeSlideshow?.();
        } else if (slider.slider) {
          slider.slider.destroy();
          slider.slider = null;
        }
      });
    }

    get cartIsEmpty() {
      return this.cartProductIds.length === 0;
    }

    /** Collection banners replace the products panel while the bag is empty. */
    get mode() {
      if (this.dataset.emptyMode === 'collections' && this.cartIsEmpty) return 'collections';
      // A rung of the ladder (how-the-cart-works §4): the strategy answer and the
      // previously shown list both came up empty, so the banners stand in.
      if (this.recoStarved && this.hasCollectionBanners) return 'collections';
      return 'products';
    }

    get hasCollectionBanners() {
      return Boolean(this.querySelector('[data-track="collections"] .ia-slider__slides')?.children.length);
    }

    get activeTrack() {
      return this.querySelector(`ia-slider[data-track="${this.mode}"]`)
        || this.querySelector('ia-slider');
    }

    /** Mode attribute plus the products heading, which the server rendered for
     *  whichever state the page was loaded in. */
    applyMode() {
      const mode = this.mode;
      this.dataset.mode = mode;
      const title = this.querySelector('[data-track="products"] .ia-cart-upsell__title');
      if (title) {
        const next = this.cartIsEmpty
          ? (this.dataset.titleEmpty || this.dataset.title)
          : this.dataset.title;
        if (next && title.textContent !== next) title.textContent = next;
      }
      return mode;
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      clearTimeout(this.renderTimer);
      if (this._onBreakpoint) SLIDER_MQ.removeEventListener('change', this._onBreakpoint);
      if (this._onVisibilityBreakpoint) DESKTOP_MQ.removeEventListener('change', this._onVisibilityBreakpoint);
    }

    getUniqueIds(ids) {
      return [...new Set(ids.map(id => String(id)))];
    }

    arraysEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, index) => val === sorted2[index]);
    }

    handleCartChanged(event) {
      const { cartObj, cartDom } = event.detail;
      if (!cartObj) return;
      // Same gate as boot: a bag change on the hidden viewport must not reach
      // the proxy either, and the crossing back re-reads the bag anyway.
      if (this.hiddenHere) {
        this.iaBootRendered = false;
        return;
      }

      // Same filter as the Liquid loops that seeded the dataset: a context the
      // two sides disagree on would refetch on the first cart change for nothing.
      const contextItems = (cartObj.items || []).filter((item) => this.countsInContext(item));
      const newCartProductIds = this.getUniqueIds(contextItems.map(item => String(item.product_id)));
      const newCartVariantIds = this.getUniqueIds(contextItems.map(item => String(item.variant_id)));
      const newBaseProductId = contextItems[0]?.product_id ? String(contextItems[0].product_id) : '';

      const newAllProductIds = this.getUniqueIds((cartObj.items || []).map(item => String(item.product_id)));
      const bagChanged = !this.arraysEqual(this.allProductIds, newAllProductIds);
      this.allProductIds = newAllProductIds;

      // Variants count too: a size swap keeps product ids but changes the context.
      const cartChanged = !this.arraysEqual(this.cartProductIds, newCartProductIds);
      const variantsChanged = !this.arraysEqual(this.cartVariantIds, newCartVariantIds);
      const baseProductChanged = newBaseProductId !== this.baseProductId;

      if (!cartChanged && !variantsChanged && !baseProductChanged) {
        // A granted line landing changes no context, but its card must still
        // dim NOW and leave at the refresh. Nothing new to ask for, so the
        // cards on screen are rebuilt as they are: no request, whatever the
        // cache is set to.
        if (bagChanged && this.hideDuplicates) {
          this.markInBag();
          this.schedulePrune();
        }
        return;
      }

      this.cartProductIds = newCartProductIds;
      this.cartVariantIds = newCartVariantIds;
      this.baseProductId = newBaseProductId;
      // A changed context is a fresh chance at real recommendations.
      this.recoStarved = false;
      // first add empties the collections panel out of the way, last removal
      // brings it back; both are pure attribute flips
      const mode = this.applyMode();
      // Entering products with no cards on screen: bridge the refresh gap with
      // the ladder's lower rungs (the last shown list, the banners, the
      // merchant's picks) instead of blanking the panel. fetchFailed keeps the
      // pass silent; the delayed render below is the real ask.
      if (mode !== 'collections' && !this.hasCards) {
        this.rebuildSlider([], true);
      }
      // What the cart is already showing is never offered beside it: the card
      // goes as soon as the change lands, not at the delayed refresh.
      if (this.hideDuplicates) this.dropInBagCards();
      this.scheduleRender(cartDom);
    }

    /**
     * Takes the cards whose product just reached the bag, in place, so the
     * strip keeps its scroll position. The tap already dimmed them; this is
     * the confirmed half, so a failed add never takes a card with it.
     */
    dropInBagCards() {
      const cards = [...this.querySelectorAll('[data-track="products"] .ia-cart-upsell__item-wrapper')];
      const stale = cards.filter((card) => this.allProductIds.includes(String(card.dataset.productId)));
      if (!stale.length) {
        this.markInBag();
        return;
      }

      // Nothing left to offer: skip the fold, the ladder decides what stands
      // in, rather than an empty strip sitting there until the refresh lands.
      if (cards.length === stale.length) {
        stale.forEach((card) => card.remove());
        this.rebuildSlider([], true, false);
        return;
      }

      // The leaver folds shut and the cards below ride up into its place; no
      // FLIP needed here, there is no reordering, the layout itself moves them.
      if (this.animates('reflow')) {
        stale.forEach((card) => collapseCard(card, () => card.remove()));
      } else {
        stale.forEach((card) => card.remove());
      }
      this.markInBag();
      // keen measured the slides it was given; it must re-measure without them
      this.activeTrack?.slider?.update?.();
    }

    /**
     * data-refresh-delay (ms): a burst of adds makes ONE request off the last
     * cart state. Only the fetch waits; pruning and the mode flip are above.
     */
    scheduleRender(cartDom) {
      this.pendingRun = 'render';
      this.afterBurst(() => {
        this.pendingRun = null;
        this.render(cartDom);
      });
    }

    /**
     * The context did not change, so there is no answer to refetch: the strip
     * is the same minus the card just added. Same debounce as a refresh, so a
     * burst still reshapes it once. A prune never downgrades a scheduled
     * render: both share one timer, and a granted line landing right after a
     * removal used to swallow the refresh that would have refilled the panel.
     */
    schedulePrune() {
      if (this.pendingRun === 'render') return;
      this.pendingRun = 'prune';
      this.afterBurst(() => {
        this.pendingRun = null;
        const cards = [...this.querySelectorAll('[data-track="products"] .ia-cart-upsell__item-wrapper')];
        this.rebuildSlider(cards, true, false);
      });
    }

    afterBurst(run) {
      const delay = parseInt(this.dataset.refreshDelay, 10) || 0;
      if (!delay) {
        run();
        return;
      }
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(run, delay);
    }

    async render(cartDom = null) {
      // Collection banners are merchant content, already in the markup: no call
      // to make, and the panel must stay visible even with zero product cards.
      if (this.applyMode() === 'collections') {
        this.panel.removeAttribute('data-reco-pending');
        this.syncSlider();
        return;
      }

      // A server render carries a fresh copy of the merchant's picks, the
      // ladder's last rung before nothing.
      this.captureFallbacks(cartDom);

      // The strip only ever shows the CURRENT bag. Two adds close together
      // leave two requests in flight, and without this the first answer paints
      // and the second repaints a beat later: two visible changes for what the
      // shopper did once. A newer request makes the older answer worthless.
      const attempt = (this.renderAttempt || 0) + 1;
      this.renderAttempt = attempt;

      let cached = null;
      let apiItems = null;
      try {
        cached = this.readRecoCache();
        apiItems = cached || (await this.fetchApiItems());
      } catch (error) {
        // The panel is hidden while it waits for this. Whatever went wrong, it
        // must not stay hidden: fall through on the ladder with what is on
        // screen, exactly as a source that answered nothing would.
        captureError(error, { where: 'cart:recommendations' });
        apiItems = null;
      }
      if (attempt !== this.renderAttempt) {
        debug('a newer cart state is already on its way, dropping this answer');
        return;
      }
      if (!cached && apiItems?.length) this.writeRecoCache(apiItems);

      // A failed refresh must not empty a panel that already has cards in it.
      // Stale recommendations beat none: the shopper otherwise watches the block
      // disappear for a reason that has nothing to do with them. They still go
      // through the ladder's own filter, so a product that reached the bag
      // LEAVES instead of sitting there greyed out until a refresh succeeds.
      if (apiItems === null && this.hasCards) {
        debug('refresh failed, re-seating the cards already on screen');
        this.rebuildSlider(this.getPreSelectedItems(), true, false);
        return;
      }

      this.rebuildSlider(apiItems || [], apiItems === null);
    }

    get hasCards() {
      return !this.panel.hasAttribute('data-reco-pending')
        && this.querySelector('[data-track="products"] .ia-cart-upsell__item-wrapper') !== null;
    }

    /**
     * Both directions, synchronously: a card whose product just entered the
     * bag dims and loses its button; one whose product left wakes back up.
     * Removal is the refresh's job (rebuildSlider filters), so the strip
     * changes shape once per burst, not once per tap. Kept cards go stale in
     * this one way that matters, so a panel we could not refresh still never
     * offers what is already in the bag.
     */
    markInBag() {
      if (this.mode === 'collections' || !this.hideDuplicates) return;
      this.querySelectorAll('[data-track="products"] .ia-cart-upsell__item-wrapper').forEach((card) => {
        const inBag = this.allProductIds.includes(String(card.dataset.productId));
        card.toggleAttribute('data-in-bag', inBag);
        card.querySelectorAll('button').forEach((button) => {
          button.disabled = inBag;
        });
      });
    }

    /** Either strategy setting works alone; whichever is filled covers both states. */
    get activeStrategy() {
      const { recoStrategy, recoStrategyEmpty } = this.dataset;
      return (this.cartVariantIds.length
        ? recoStrategy || recoStrategyEmpty
        : recoStrategyEmpty || recoStrategy) || '';
    }

    /**
     * Per-category opt-ins (data-reco-count tokens), all off by default: the
     * strip answers what the shopper chose unless the merchant says otherwise.
     * Liquid mirror: the reco context loop in ia-cart-upsell.liquid.
     */
    countsInContext(item) {
      // A pre-added default the shopper never picked is bookkeeping: the list
      // never renders it (countsTowardBag, ia-cart-api.js), so it must not make
      // an empty-looking bag read as busy. Liquid mirror: ia-cart-upsell.liquid.
      if (item.properties?._gwp_auto || item.properties?._sample_auto) return false;
      const counted = this.recoCounted;
      let counts = true;
      if (item.product_type === 'insurance') counts = counted.has('protection');
      else if (item.product_type === 'Passport Reward') counts = counted.has('rewards');
      else if (isSampleLine(item)) counts = counted.has('samples');
      else if (isGrantedLine(item)) counts = counted.has('gifts');
      // $0 catches free items whose type is none of the families above. The
      // ORIGINAL price, never the final one: a discount code allocated to the
      // line would otherwise erase an ordinary purchase from the context.
      if (counts && item.original_price === 0 && !counted.has('zero')) return false;
      return counts;
    }

    get recoCounted() {
      return new Set((this.dataset.recoCount || '').split(',').filter(Boolean));
    }

    /**
     * Theme settings -> IA Cart -> Animations. Reduced motion always wins, and
     * the mobile strip is keen's: it owns the slides' transforms, so ours
     * would be overwritten mid-flight.
     */
    animates(name) {
      if (REDUCED_MOTION.matches || SLIDER_MQ.matches) return false;
      return this.hasAttribute(`data-anim-${name}`);
    }

    /** What the current mutation is allowed to animate, for animateStrip. */
    get stripWants() {
      return { move: this.animates('reflow'), enter: this.animates('fade') };
    }

    /** "Hide products already in the bag": pruning and the results filter. */
    get hideDuplicates() {
      return this.dataset.keepDuplicates !== 'true';
    }

    /** "Keep the previous list" (data-keep-previous), the ladder's second rung. */
    get keepPrevious() {
      return this.dataset.keepPrevious !== 'false';
    }

    /**
     * Wanted recommendations, got none. The shopper is covered by the rung that
     * answered; the team still hears, because an empty answer is always worth a
     * look. A failed fetch already reported as cart:recommendations.
     */
    reportEmpty(fetchFailed, shown) {
      if (fetchFailed) return;
      const empty = new Error('recommendations came up empty for a bag that wanted them');
      console.error('[ia-cart-upsell]', empty.message);
      captureError(empty, {
        where: 'cart:reco-empty',
        // The rung that ran out is the merchandising's news; the panel simply
        // stays closed and the bag is untouched.
        level: 'warning',
        strategy: this.activeStrategy || 'shopify',
        shown,
        context: this.cartVariantIds.length,
      });
    }

    /**
     * The Liquid loop and countsInContext are two answers to one question, and
     * a drift between them costs a needless refetch on the first cart change
     * and a context the merchant did not configure. Compared once per load
     * against the same seeded cart Liquid rendered from.
     */
    checkContextMirror() {
      let seeded = null;
      try {
        seeded = JSON.parse(document.getElementById('ia-cart-json')?.textContent);
      } catch {
        return;
      }
      if (!seeded?.items) return;
      const mine = this.getUniqueIds(
        seeded.items.filter((item) => this.countsInContext(item)).map((item) => String(item.product_id))
      );
      if (this.arraysEqual(mine, this.cartProductIds)) return;
      const drift = new Error('the Liquid and JS recommendation contexts disagree on one cart');
      console.error('[ia-cart-upsell]', drift.message, { liquid: this.cartProductIds, js: mine });
      captureError(drift, {
        where: 'cart:reco-context',
        liquid: this.cartProductIds.join(','),
        js: mine.join(','),
      });
    }

    /** What must match for a cached strip to still be the right answer. */
    get recoSignature() {
      const { recoEndpoint, recoSource, limit, intent } = this.dataset;
      const source = recoEndpoint && this.activeStrategy
        ? ['proxy', this.activeStrategy, recoSource || '']
        : ['shopify', this.baseProductId, limit || '', intent || ''];
      // The filter settings are part of the answer: without them a merchant's
      // flip kept replaying a strip rendered under the OLD settings for a TTL.
      const filters = [
        this.dataset.recoVisible || '',
        this.dataset.recoCount || '',
        this.hideDuplicates ? 'hide' : 'keep',
      ];
      // And the shopper: a repurchase strategy answers from their own orders,
      // and sessionStorage outlives a logout in the same tab.
      const who = this.dataset.customer || '';
      return [...source, ...filters, who, [...this.cartVariantIds].sort().join(',')].join('|');
    }

    /**
     * Rendered cards, in MINUTES (data-reco-cards-ttl). This markup carries
     * price, stock and badges, so it is the short one; the default is 0, cards
     * redrawn every page. The picks below are the long one.
     */
    get recoCardsTtl() {
      return ttlFromAttribute(this.dataset.recoCardsTtl, RECO_CARDS_TTL_MS);
    }

    /** The proxy's answer, in MINUTES (data-abtasty-cache-ttl): ids, no prices. */
    get recoPicks() {
      return {
        slot: `${RECO_CACHE_SLOT}:picks`,
        signature: this.recoSignature,
        ttl: ttlFromAttribute(this.dataset.abtastyCacheTtl, RECO_PICKS_TTL_MS),
      };
    }

    readRecoCache() {
      const html = readCached(RECO_CACHE_SLOT, this.recoSignature, this.recoCardsTtl);
      if (!html) return null;
      const parser = document.createElement('div');
      parser.innerHTML = html;
      const items = [...parser.children];
      return items.length ? items : null;
    }

    writeRecoCache(items) {
      writeCached(
        RECO_CACHE_SLOT,
        this.recoSignature,
        this.recoCardsTtl,
        items.map((item) => item.outerHTML).join('')
      );
    }

    /** A strategy wins, and needs nothing in the cart: an empty cart just sends no context. */
    fetchApiItems() {
      const { recoEndpoint, recoSource } = this.dataset;
      const strategy = this.activeStrategy;
      if (strategy && recoEndpoint) {
        return AbTastyAPI.fetch(this.cartVariantIds, {
          endpoint: recoEndpoint,
          strategy,
          source: recoSource,
          picks: this.recoPicks,
        });
      }

      if (this.enableDynamic && this.baseProductId) {
        return RecommendationsAPI.fetch(this.baseProductId, {
          limit: parseInt(this.dataset.limit) || 10,
          intent: this.dataset.intent || 'complementary',
          sectionId: this.dataset.sectionId
        });
      }

      return Promise.resolve([]);
    }

    getPreSelectedItems(cartDom) {
      const source = cartDom?.querySelector('ia-cart-upsell') || this;
      return Array.from(source.querySelectorAll('[data-track="products"] .ia-cart-upsell__item-wrapper'));
    }

    /**
     * The merchant's picks are the only cards the server puts in the products
     * track, so a server render is the one place they can be read cleanly.
     * Cloned, because the live nodes are about to be replaced by a rebuild.
     */
    captureFallbacks(cartDom) {
      const cards = this.getPreSelectedItems(cartDom);
      if (cards.length) this.fallbackCards = cards.map((card) => card.cloneNode(true));
    }

    /**
     * The visible column is the section's wrapper, which carries the width and the
     * white background. Hiding this element instead leaves that box on screen.
     */
    get panel() {
      return this.closest('.ia-cart__upsell-section') || this;
    }

    /** Cards this cart may still be offered: the in-bag filter, one place. */
    usableCards(list) {
      return (list || []).filter((item) => {
        const productId = item.dataset?.productId;
        if (!productId) return false;
        return !this.hideDuplicates || !this.allProductIds.includes(String(productId));
      });
    }

    /**
     * The ladder, in order (how-the-cart-works §4): this cart's own answer, the last
     * list the strategy gave, the empty-cart banners, the merchant's picks.
     * An empty panel is never shown: with every rung dry the block hides.
     *
     * `promote` is false for callers that pass cards already on screen (the
     * prune): re-seating them must not turn the server's picks into "the
     * previous list", or they would outrank the banners on the next dry round.
     */
    rebuildSlider(items, fetchFailed = false, promote = true) {
      let filtered = this.usableCards(items);
      let rung = 'strategy';

      if (!filtered.length && this.keepPrevious) {
        filtered = this.usableCards(this.lastShownCards);
        rung = 'previous';
      }

      if (!filtered.length && this.hasCollectionBanners) {
        this.recoStarved = true;
        this.applyMode();
        this.panel.removeAttribute('data-reco-pending');
        this.syncSlider();
        this.reportEmpty(fetchFailed, 'empty-cart-banners');
        return;
      }

      if (!filtered.length) {
        filtered = this.usableCards(this.fallbackCards);
        rung = 'fallback';
      }

      if (!filtered.length) {
        this.recoStarved = true;
        this.panel.setAttribute('data-reco-pending', '');
        this.reportEmpty(fetchFailed, 'nothing');
        return;
      }

      debug('ladder rung', { rung, cards: filtered.length });
      this.recoStarved = false;
      // Only a real answer becomes the "previous list": seeding it from the
      // server's markup would make the merchant's picks outrank the banners,
      // which is not the order the ladder promises.
      if (promote && rung === 'strategy') this.lastShownCards = filtered;
      this.buildTrack(filtered);
    }

    /**
     * The DOM half of a rebuild, reconciled rather than wiped: a product
     * already on screen keeps its OWN element, so its image is never
     * re-requested and keen is not thrown away. Wiping the track showed as a
     * flicker on every load, the boot render replacing server-painted cards
     * with fresh clones of the same thing.
     */
    buildTrack(filtered) {
      let track = this.querySelector('ia-slider[data-track="products"]');
      if (!track) {
        if (!this.sliderTemplate) return;
        const temp = document.createElement('div');
        temp.innerHTML = this.sliderTemplate;
        track = temp.querySelector('ia-slider');
        // Prepended, never appended: the collections track lives beside this
        // one and must keep its place.
        if (track) this.prepend(track);
      }
      const slides = track?.querySelector('.ia-slider__slides');
      if (!slides) return;

      const before = [...slides.children];
      const existingById = new Map();
      before.forEach((card) => {
        const id = card.dataset.productId;
        if (id && !existingById.has(id)) existingById.set(id, card);
      });

      const wanted = filtered.map((incoming) => {
        const existing = existingById.get(incoming.dataset.productId);
        if (existing && cardContent(existing) === cardContent(incoming)) return existing;
        // A card that is already a child came from getPreSelectedItems: reuse
        // the node itself, cloning it would repaint for nothing.
        return incoming.parentElement === slides ? incoming : incoming.cloneNode(true);
      });

      const keep = new Set(wanted);
      // appendChild MOVES a node that is already here, so reordering costs no
      // repaint; only the genuinely new nodes are parsed into the document.
      // It also collapses the duplicates the caller hands us: the boot render
      // passes the fetched cards AND the ones already on screen.
      animateStrip(slides, () => {
        wanted.forEach((card) => slides.appendChild(card));
        before.forEach((card) => { if (!keep.has(card)) card.remove(); });
      }, this.stripWants);

      // Read the result, not the request: the wanted list carries those
      // duplicates, so its length says nothing about what moved.
      const after = [...slides.children];
      const changed = before.length !== after.length
        || before.some((card, index) => card !== after[index]);

      this.panel.removeAttribute('data-reco-pending');
      this.applyMode();
      // Kept nodes carry whatever dimming they wore; the bag decides it, not
      // the markup. Matters where in-bag cards are kept on purpose.
      this.markInBag();
      this.syncSlider();
      if (!changed) return;
      // keen measured the slides it was given, so it has to re-measure the new
      // set; a strip that did not move needs neither this nor the scroll.
      track.slider?.update?.();
      // The scroll exists so a NEW list reads from its first card. With the
      // top card unchanged, the shopper's anchor is intact and the change
      // below announces itself with its own movement: yanking them up would
      // undo their place for nothing.
      if (before[0] !== after[0]) this.scrollBackToTop();
    }

    /**
     * Desktop refresh courtesy: the column returns to its first card instead
     * of wherever the shopper left off in the OLD strip. Merchant-set
     * (data-refresh-scroll: default smooth, `instant`, `off`); reduced motion
     * always jumps instantly, the behaviour itself stays.
     */
    scrollBackToTop() {
      if (SLIDER_MQ.matches) return; // the mobile strip is keen's, not a column
      if (this.dataset.refreshScroll === 'off') return;
      const panel = this.panel;
      const smooth = this.dataset.refreshScroll !== 'instant'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const from = panel.scrollTop;
      panel.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
      if (!smooth || !from) return;
      // A smooth scroll rides animation frames, and a background tab has
      // none: if nothing moved shortly, jump. The return beats the glide.
      clearTimeout(this.scrollFallbackTimer);
      this.scrollFallbackTimer = setTimeout(() => {
        if (panel.scrollTop >= from) panel.scrollTo({ top: 0 });
      }, 250);
    }
  }

  customElements.define('ia-cart-upsell', IaCartUpsell);
}
